import { Injectable } from "@angular/core";

import { filter, take } from "rxjs/operators";
import * as Sentry from "@sentry/angular";
import { environment } from "src/environments/environment";
import { Device } from "@capacitor/device";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import { AnalyticsSlugs } from "./analytics-slugs";
import { BUILD, VERSION } from "src/autogenerated-version";

import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { GetUserProfileUseCase } from "@app/core/user/domain/use-cases/get-user-profile.use-case";
import { User } from "@app/core/user/domain/user";

// Firebase/GA4 hard limits — events violating them are silently truncated or
// dropped server-side, corrupting the data (see aiball_app#288).
const MAX_EVENT_NAME_LENGTH = 40;
const MAX_PARAM_NAME_LENGTH = 40;
const MAX_PARAM_VALUE_LENGTH = 100;
const MAX_PARAMS_PER_EVENT = 25;
const VALID_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;
// User properties have their own, stricter limits than events.
const MAX_USER_PROPERTY_NAME_LENGTH = 24;
const MAX_USER_PROPERTY_VALUE_LENGTH = 36;

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  analyticsEnabled = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private getUserProfile: GetUserProfileUseCase
  ) {}

  async initAnalytics() {
    if (this.isAnalyticsEnabled()) {
      if ((await Device.getInfo()).platform == "web") {
        await FirebaseAnalytics.initializeFirebase(environment.FIREBASE_CONFIG);
      }
    } else {
      if ((await Device.getInfo()).platform != "web")
        await this.disableAnalytics();
    }
    this.setUserToAnalytics();
    this.setAppVersion();
    this.sendEachRouteToAnalytics();
  }

  setUserToAnalytics() {
    this.getUserProfile
      .execute()
      .pipe(take(1))
      .subscribe({
        next: (user: User) => {
          this.setUser(user.email);
        },
        error: () => {},
      });
  }

  sendEachRouteToAnalytics() {
    //This is in order to get the route like /match/:id
    //and send the id of the match as a param
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const params = {
          aiball_screen: this.getCurrentRouteSlug(),
          aiball_url: e.url,
        };
        this.setScreenName(params);
      });
  }

  getCurrentRouteSlug() {
    let currentRoute = this.router.routerState.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return "/" + currentRoute.routeConfig?.path;
  }

  isAnalyticsEnabled() {
    return environment.ANALYTICS_ENABLED;
  }

  isAnalyticsDisabled() {
    return !this.isAnalyticsEnabled();
  }

  isDev() {
    return this.getEnvironment() === "DEV";
  }

  getEnvironment() {
    return environment.NAME;
  }

  async enableAnalytics() {
    await FirebaseAnalytics.setCollectionEnabled({
      enabled: true,
    });
  }

  async disableAnalytics() {
    await FirebaseAnalytics.setCollectionEnabled({
      enabled: false,
    });
  }

  async setUser(email: string) {
    if (this.isAnalyticsDisabled()) return;
    // Sets the user ID property.
    await FirebaseAnalytics.setUserId({
      userId: email,
    });
  }

  /**
   * Reports the running app version to GA4.
   *
   * Firebase fills `app_info.version` by itself on iOS and Android, but not on
   * web: there the field arrives NULL, so version adoption for the web app
   * cannot be measured at all, and an old web client hitting a route only the
   * new one understands is indistinguishable from a healthy session
   * (padel_ia#1125).
   *
   * The property is set on every platform, not just web, so that a single
   * query covers all three instead of having to read `app_info.version` on
   * native and a user property on web.
   */
  setAppVersion() {
    if (this.isAnalyticsDisabled()) return;
    this.setUserProperty("app_version", VERSION);
    this.setUserProperty("app_build", String(BUILD));
  }

  private setUserProperty(name: string, value: string) {
    this.assertValidUserProperty(name, value);
    FirebaseAnalytics.setUserProperty({ name, value });
  }

  private assertValidUserProperty(name: string, value: string) {
    const violations: string[] = [];
    if (name.length > MAX_USER_PROPERTY_NAME_LENGTH)
      violations.push(
        `user property name "${name}" is ${name.length} chars (max ${MAX_USER_PROPERTY_NAME_LENGTH})`
      );
    if (!VALID_NAME_PATTERN.test(name))
      violations.push(`user property name "${name}" has invalid characters`);
    if (value.length > MAX_USER_PROPERTY_VALUE_LENGTH)
      violations.push(
        `user property "${name}" value is ${value.length} chars (max ${MAX_USER_PROPERTY_VALUE_LENGTH})`
      );
    if (violations.length === 0) return;

    const message = `Invalid analytics user property: ${violations.join("; ")}`;
    if (this.isDev()) throw new Error(message);
    Sentry.captureMessage(message, { level: "warning" });
  }

  setProperty() {
    if (this.isAnalyticsDisabled()) return;
    //sets a user property to a given value.
    FirebaseAnalytics.setUserProperty({
      name: "framework",
      value: "angular",
    });
  }

  logEvent(logEvent: AnalyticsSlugs, params: Record<string, unknown> = {}) {
    if (this.isAnalyticsDisabled()) return;
    this.assertValidEvent(logEvent, params);
    //Logs an app event.
    FirebaseAnalytics.logEvent({
      name: logEvent,
      params: params,
    });
  }

  private assertValidEvent(name: string, params: Record<string, unknown>) {
    const violations: string[] = [];
    if (name.length > MAX_EVENT_NAME_LENGTH)
      violations.push(
        `event name "${name}" is ${name.length} chars (max ${MAX_EVENT_NAME_LENGTH})`
      );
    if (!VALID_NAME_PATTERN.test(name))
      violations.push(`event name "${name}" has invalid characters`);
    const entries = Object.entries(params);
    if (entries.length > MAX_PARAMS_PER_EVENT)
      violations.push(`${entries.length} params (max ${MAX_PARAMS_PER_EVENT})`);
    for (const [key, value] of entries) {
      if (key.length > MAX_PARAM_NAME_LENGTH || !VALID_NAME_PATTERN.test(key))
        violations.push(`invalid param name "${key}"`);
      if (typeof value === "string" && value.length > MAX_PARAM_VALUE_LENGTH)
        violations.push(
          `param "${key}" value is ${value.length} chars (max ${MAX_PARAM_VALUE_LENGTH})`
        );
    }
    if (violations.length === 0) return;

    const message = `Invalid analytics event: ${violations.join("; ")}`;
    if (this.isDev()) throw new Error(message);
    // In production keep sending (Firebase truncates rather than drops) but
    // make the corruption visible instead of silent.
    Sentry.captureMessage(message, { level: "warning" });
  }

  setScreenName(params: Record<string, unknown> = {}) {
    if (this.isAnalyticsDisabled()) return;
    this.logEvent(AnalyticsSlugs.SCREEN_VIEW, params);
    //We dont use the FirebaseAnalytics.setScreenName
    //because we can't add parameters with setScreenName method
    //and we want the route with the url slut
  }

  toggleAnalytics() {
    if (this.isAnalyticsDisabled()) return;
    this.analyticsEnabled = !this.analyticsEnabled;
    FirebaseAnalytics.setCollectionEnabled({
      enabled: this.analyticsEnabled,
    });
  }
}
